import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../context/authContext";
import { HouseContext } from "../../../context/HouseContext";
import { AlquileresContext } from "../../../context/AlquileresContext";
import { NotasContext } from "../../../context/NotasContext";
import { ActividadContext } from "../../../context/ActividadContext";
import { ThemeProvider, ThemeContext } from "../../../context/ThemeContext";
import { FaRightFromBracket } from "react-icons/fa6";
import {
  FaHome,
  FaBuilding,
  FaPlusCircle,
  FaStar,
  FaSun,
  FaMoon,
  FaChevronDown,
  FaChevronRight,
  FaFileContract,
  FaUserTie,
  FaUsers,
  FaDollarSign,
  FaChartLine,
  FaCog,
  FaClipboardList,
  FaMoneyBillWave,
  FaChartBar,
  FaStickyNote,
  FaBell,
  FaExternalLinkAlt,
} from "react-icons/fa";
import Logo from "../../../assets/img/logos/logoColor.png";

// "hace 2 dias" a partir de un ISO string (igual que en Notas.jsx).
const tiempoRelativo = (iso) => {
  if (!iso) return "";
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return "";
  const minutos = Math.floor((Date.now() - fecha.getTime()) / 60000);
  if (minutos < 1) return "recien";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} dias`;
  return fecha.toLocaleDateString("es-AR");
};

const navItems = [
  { to: "/dashboard", label: "Home", icon: FaHome, exact: true },
  { to: "/dashboard/propiedades", label: "Propiedades", icon: FaBuilding, showCount: true },
  { to: "/dashboard/propiedades/agregar", label: "Agregar", icon: FaPlusCircle },
  { to: "/dashboard/propiedades/destacadas", label: "Destacadas", icon: FaStar },
];

const alquileresSubItems = [
  { to: "/dashboard/alquileres", label: "Resumen", icon: FaClipboardList, exact: true },
  { to: "/dashboard/alquileres/contratos", label: "Contratos", icon: FaFileContract },
  { to: "/dashboard/alquileres/locadores", label: "Locadores (Propietario)", icon: FaUserTie },
  { to: "/dashboard/alquileres/locatarios", label: "Locatarios (Inquilino)", icon: FaUsers },
  { to: "/dashboard/alquileres/cobros", label: "Cobros (Inquilino)", icon: FaMoneyBillWave },
  { to: "/dashboard/alquileres/liquidacion", label: "Pagos (Propietario)", icon: FaDollarSign },
  { to: "/dashboard/alquileres/aumentos", label: "Aumentos", icon: FaChartLine },
  { to: "/dashboard/alquileres/reportes", label: "Reportes", icon: FaChartBar },
  { to: "/dashboard/alquileres/configuracion", label: "Configuracion", icon: FaCog },
];

function DashboardContent({ children }) {
  const { logout, user } = useAuthContext();
  const { allHouses } = useContext(HouseContext);
  const { contratosPorVencer } = useContext(AlquileresContext);
  const { notasVencidas } = useContext(NotasContext);
  const { actividad, actividadNoLeida, marcarLeida, marcarTodoLeido } =
    useContext(ActividadContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [alquileresOpen, setAlquileresOpen] = useState(
    location.pathname.startsWith("/dashboard/alquileres")
  );
  const [actividadOpen, setActividadOpen] = useState(false);
  const actividadMenuRef = useRef(null);

  useEffect(() => {
    if (!actividadOpen) return;
    const onClickOutside = (e) => {
      if (actividadMenuRef.current && !actividadMenuRef.current.contains(e.target)) {
        setActividadOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [actividadOpen]);

  const ultimaActividad = useMemo(
    () =>
      [...actividad]
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
        .slice(0, 8),
    [actividad]
  );

  const handleVerTodaActividad = () => {
    setActividadOpen(false);
    navigate("/dashboard/actividad");
  };

  const handleLogout = async () => {
    await logout();
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  };

  const isAlquileresActive = location.pathname.startsWith("/dashboard/alquileres");
  const isNotasActive = location.pathname.startsWith("/dashboard/notas");
  const isActividadActive = location.pathname.startsWith("/dashboard/actividad");

  const linkClasses = (active) =>
    `text-base font-normal rounded-lg flex items-center p-2 transition duration-75 ${
      active
        ? "bg-primary text-white"
        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
    }`;

  const iconClasses = (active) =>
    `w-4 h-4 ${active ? "text-white" : "text-gray-500 dark:text-gray-400"}`;

  return (
    <div>
      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed top-0 left-0 z-30 w-full">
        <div className="px-3 py-5 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-between w-full">
              <Link
                to="/dashboard"
                className="text-xl font-bold flex items-center lg:ml-2.5 dark:text-gray-100"
              >
                <img src={Logo} className="h-6 mr-2" alt="Logo" />
                <span className="self-center whitespace-nowrap">
                  Inmobiliaria
                </span>
              </Link>
              <div className="flex items-center gap-2">
                <a
                  href="/?vista=publica"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  title="Abrir el sitio publico en una pestaña nueva"
                >
                  <FaExternalLinkAlt className="w-3.5 h-3.5" />
                  Ver sitio
                </a>
                <div className="relative" ref={actividadMenuRef}>
                  <button
                    onClick={() => setActividadOpen((v) => !v)}
                    className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    title="Actividad"
                  >
                    <FaBell className="w-5 h-5" />
                    {actividadNoLeida.length > 0 && (
                      <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                        {actividadNoLeida.length > 9 ? "9+" : actividadNoLeida.length}
                      </span>
                    )}
                  </button>
                  {actividadOpen && (
                    <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                          Actividad
                        </span>
                        {actividadNoLeida.length > 0 && (
                          <button
                            onClick={marcarTodoLeido}
                            className="text-xs text-primary hover:underline"
                          >
                            Marcar todo leido
                          </button>
                        )}
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {ultimaActividad.length === 0 ? (
                          <p className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                            Todavia no hay actividad registrada.
                          </p>
                        ) : (
                          ultimaActividad.map((a) => {
                            const noLeida = !a.leidoPor?.includes(user?.uid);
                            return (
                              <button
                                key={a.idFirebase}
                                onClick={() => marcarLeida(a.idFirebase)}
                                className={`w-full text-left px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                                  noLeida ? "bg-primary/5 dark:bg-primary/10" : ""
                                }`}
                              >
                                <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">
                                  {a.mensaje}
                                </p>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                                  {a.autorNombre || "Sistema"} &middot; {tiempoRelativo(a.createdAt)}
                                </p>
                              </button>
                            );
                          })
                        )}
                      </div>
                      <button
                        onClick={handleVerTodaActividad}
                        className="w-full text-center text-sm text-primary hover:bg-gray-50 dark:hover:bg-gray-700/50 py-2.5 border-t border-gray-100 dark:border-gray-700"
                      >
                        Ver toda la actividad
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  title={theme === "dark" ? "Modo claro" : "Modo oscuro"}
                >
                  {theme === "dark" ? (
                    <FaSun className="w-5 h-5" />
                  ) : (
                    <FaMoon className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="lg:hidden focus:outline-none mr-4 cursor-pointer dark:text-gray-100"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={
                        isOpen
                          ? "M6 18L18 6M6 6l12 12"
                          : "M4 6h16M4 12h16m-7 6h7"
                      }
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="flex overflow-hidden bg-white dark:bg-gray-900 pt-16">
        <aside
          className={`fixed z-20 h-full top-0 left-0 pt-16 flex flex-shrink-0 flex-col w-64 transition-transform duration-75 ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        >
          <div className="relative flex-1 flex flex-col min-h-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pt-0">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex-1 px-3 bg-white dark:bg-gray-800 space-y-1">
                {/* Propiedades nav */}
                <ul className="space-y-1 pb-2">
                  {navItems.map((item) => {
                    const active = isActive(item);
                    const Icon = item.icon;
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={() => setIsOpen(false)}
                          className={linkClasses(active)}
                        >
                          <Icon className={iconClasses(active)} />
                          <span className="ml-3 flex-1 whitespace-nowrap text-sm">
                            {item.label}
                          </span>
                          {item.showCount && allHouses && (
                            <span
                              className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
                                active
                                  ? "bg-white/20 text-white"
                                  : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                              }`}
                            >
                              {allHouses.length}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>

                {/* Alquileres collapsible section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                  <button
                    onClick={() => setAlquileresOpen(!alquileresOpen)}
                    className={`w-full text-base font-normal rounded-lg flex items-center p-2 transition duration-75 ${
                      isAlquileresActive && !alquileresOpen
                        ? "bg-primary/10 text-primary dark:text-accent"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <FaFileContract
                      className={`w-4 h-4 ${
                        isAlquileresActive
                          ? "text-primary dark:text-accent"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    />
                    <span className="ml-3 flex-1 text-left text-sm font-medium">
                      Alquileres
                    </span>
                    {contratosPorVencer?.length > 0 && (
                      <span className="mr-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {contratosPorVencer.length}
                      </span>
                    )}
                    {alquileresOpen ? (
                      <FaChevronDown className="w-3 h-3 text-gray-400" />
                    ) : (
                      <FaChevronRight className="w-3 h-3 text-gray-400" />
                    )}
                  </button>
                  {alquileresOpen && (
                    <ul className="mt-1 ml-2 space-y-0.5">
                      {alquileresSubItems.map((item) => {
                        const active = isActive(item);
                        const Icon = item.icon;
                        return (
                          <li key={item.to}>
                            <Link
                              to={item.to}
                              onClick={() => setIsOpen(false)}
                              className={`text-sm font-normal rounded-lg flex items-center px-2 py-1.5 transition duration-75 ${
                                active
                                  ? "bg-primary text-white"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                              }`}
                            >
                              <Icon
                                className={`w-3.5 h-3.5 ${
                                  active ? "text-white" : "text-gray-400 dark:text-gray-500"
                                }`}
                              />
                              <span className="ml-2.5">{item.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Notas */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                  <Link
                    to="/dashboard/notas"
                    onClick={() => setIsOpen(false)}
                    className={linkClasses(isNotasActive)}
                  >
                    <FaStickyNote className={iconClasses(isNotasActive)} />
                    <span className="ml-3 flex-1 whitespace-nowrap text-sm">Notas</span>
                    {notasVencidas?.length > 0 && (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isNotasActive
                            ? "bg-white/20 text-white"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                        }`}
                      >
                        {notasVencidas.length}
                      </span>
                    )}
                  </Link>
                </div>

                {/* Actividad */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                  <Link
                    to="/dashboard/actividad"
                    onClick={() => setIsOpen(false)}
                    className={linkClasses(isActividadActive)}
                  >
                    <FaBell className={iconClasses(isActividadActive)} />
                    <span className="ml-3 flex-1 whitespace-nowrap text-sm">Actividad</span>
                    {actividadNoLeida.length > 0 && (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isActividadActive
                            ? "bg-white/20 text-white"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                        }`}
                      >
                        {actividadNoLeida.length}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
              <div className="space-y-2 pt-2 px-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center p-2 rounded-lg group w-full"
                  onClick={handleLogout}
                >
                  <FaRightFromBracket className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition duration-75" />
                  <span className="ml-3 font-medium">Salir</span>
                </button>
              </div>
            </div>
          </div>
        </aside>
        <div
          className={`bg-gray-900 opacity-50 fixed inset-0 z-10 ${
            isOpen ? "block" : "hidden"
          } lg:hidden`}
          onClick={() => setIsOpen(false)}
        ></div>
        <div
          className="h-[93vh] w-full min-w-0 bg-gray-50 dark:bg-gray-900 relative overflow-x-hidden overflow-y-auto lg:ml-64 py-6"
        >
          <main>{children}</main>
        </div>
      </div>
    </div>
  );
}

function DashboardContainer({ children }) {
  return (
    <ThemeProvider>
      <DashboardContent>{children}</DashboardContent>
    </ThemeProvider>
  );
}

export default DashboardContainer;
